import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import '../styles/NewsEditor.css';

const NewsEditor = ({ news, onSave, onCancel }) => {
    const { user } = useAuth();
    const [title, setTitle] = useState('');
    const [summary, setSummary] = useState('');
    const [content, setContent] = useState('');
    const [coverImage, setCoverImage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const contentRef = useRef(null);

    useEffect(() => {
        if (news) {
            setTitle(news.title);
            setSummary(news.summary);
            setContent(news.content);
            setCoverImage(news.cover_image || '');
        }
    }, [news]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const url = news ? `/api/news/${news.id}` : '/api/news';
            const method = news ? 'PUT' : 'POST';
            
            const body = {
                title,
                summary,
                content,
                coverImage,
                userId: user.id
            };

            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(body),
            });

            const data = await response.json();
            
            if (data.success) {
                alert(news ? 'Noticia actualizada exitosamente' : 'Noticia creada exitosamente');
                onSave();
            } else {
                alert('Error guardando la noticia: ' + data.message);
            }
        } catch (error) {
            console.error('Error saving news:', error);
            alert('Error guardando la noticia');
        } finally {
            setIsSubmitting(false);
        }
    };

    const formatText = (command, value = null) => {
        document.execCommand(command, false, value);
        contentRef.current.focus();
    };

    const handleContentChange = () => {
        setContent(contentRef.current.innerHTML);
    };

    const insertLink = () => {
        const url = prompt('Ingrese la URL del enlace:');
        if (url) {
            formatText('createLink', url);
        }
    };

    const insertImage = () => {
        const url = prompt('Ingrese la URL de la imagen:');
        if (url) {
            formatText('insertImage', url);
        }
    };

    return (
        <div className="news-editor">
            <div className="editor-header">
                <h3>{news ? 'Editar Noticia' : 'Nueva Noticia'}</h3>
                <div className="editor-actions">
                    <button type="button" onClick={onCancel} className="cancel-btn">
                        Cancelar
                    </button>
                    <button 
                        type="submit" 
                        form="newsForm"
                        className="save-btn"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Guardando...' : 'Guardar'}
                    </button>
                </div>
            </div>

            <form id="newsForm" onSubmit={handleSubmit} className="editor-form">
                <div className="form-group">
                    <label htmlFor="title">Título *</label>
                    <input
                        type="text"
                        id="title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        required
                        placeholder="Ingrese el título de la noticia"
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="summary">Resumen Corto *</label>
                    <textarea
                        id="summary"
                        value={summary}
                        onChange={(e) => setSummary(e.target.value)}
                        required
                        placeholder="Ingrese un resumen corto de la noticia"
                        rows="3"
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="coverImage">Imagen de Portada (URL)</label>
                    <input
                        type="url"
                        id="coverImage"
                        value={coverImage}
                        onChange={(e) => setCoverImage(e.target.value)}
                        placeholder="https://ejemplo.com/imagen.jpg"
                    />
                    {coverImage && (
                        <div className="image-preview">
                            <img src={coverImage} alt="Vista previa" />
                        </div>
                    )}
                </div>

                <div className="form-group">
                    <label>Contenido *</label>
                    <div className="editor-toolbar">
                        <button type="button" onClick={() => formatText('bold')} title="Negrita">
                            <b>B</b>
                        </button>
                        <button type="button" onClick={() => formatText('italic')} title="Cursiva">
                            <i>I</i>
                        </button>
                        <button type="button" onClick={() => formatText('underline')} title="Subrayado">
                            <u>U</u>
                        </button>
                        <button type="button" onClick={() => formatText('strikethrough')} title="Tachado">
                            <s>S</s>
                        </button>
                        <span className="separator">|</span>
                        <button type="button" onClick={() => formatText('justifyLeft')} title="Alinear izquierda">
                            ◀
                        </button>
                        <button type="button" onClick={() => formatText('justifyCenter')} title="Centrar">
                            ▬
                        </button>
                        <button type="button" onClick={() => formatText('justifyRight')} title="Alinear derecha">
                            ▶
                        </button>
                        <span className="separator">|</span>
                        <button type="button" onClick={() => formatText('insertUnorderedList')} title="Lista con viñetas">
                            • Lista
                        </button>
                        <button type="button" onClick={() => formatText('insertOrderedList')} title="Lista numerada">
                            1. Lista
                        </button>
                        <span className="separator">|</span>
                        <button type="button" onClick={insertLink} title="Insertar enlace">
                            🔗
                        </button>
                        <button type="button" onClick={insertImage} title="Insertar imagen">
                            🖼️
                        </button>
                        <span className="separator">|</span>
                        <button type="button" onClick={() => formatText('formatBlock', 'h2')} title="Título grande">
                            H2
                        </button>
                        <button type="button" onClick={() => formatText('formatBlock', 'h3')} title="Título mediano">
                            H3
                        </button>
                        <button type="button" onClick={() => formatText('formatBlock', 'p')} title="Párrafo">
                            P
                        </button>
                    </div>
                    <div
                        ref={contentRef}
                        className="editor-content"
                        contentEditable
                        onInput={handleContentChange}
                        dangerouslySetInnerHTML={{ __html: content }}
                        placeholder="Escriba el contenido de la noticia aquí..."
                    />
                </div>
            </form>
        </div>
    );
};

export default NewsEditor;
